import { LitElement, html } from 'lit';

import dispatchCustomEvent from '../../utils/dispatch-custom-event';
import translate from '../../utils/webcomponents/translate';
import constructDialog from '../../utils/webcomponents/construct-dialog';

import alertEvents from './../alert/alert-events';
import watchlistEvents from './watchlist-events';
import * as watchlistApi from './watchlist-api';

import './watchlist-table';

class Watchlist extends LitElement {
	static get properties() {
		return {
			name: { type: String },
			currency: { type: String },
			hasLoaded: { type: Boolean },
			isLoading: { type: Boolean },
			isError: { type: Boolean },
			items: { type: Array },
			showIsinColumn: { type: Boolean },
		};
	}

	constructor() {
		super();

		this.items = [];
		this.showIsinColumn = false;

		this.hasLoaded = false;
		this.isLoading = false;
		this.isError = false;
	}

	async load() {
		this.isLoading = true;
		this.isError = false;

		try {
			const { items, showIsinColumn } = await watchlistApi.get();

			this.items = this.sortItems(items);

			this.showIsinColumn = showIsinColumn;

			const isEmpty = this.items.length === 0;

			this.emptyMessageContainer.classList.toggle('is-hidden', !isEmpty);
		} catch (e) {
			console.warn(e);

			this.isError = true;
		} finally {
			this.isLoading = false;
			this.hasLoaded = true;
		}
	}

	sortItems(items = []) {
		const inactiveItems = items.filter((item) => item.isProductInactive);
		const activeItems = items.filter((item) => !item.isProductInactive);

		return [...activeItems, ...inactiveItems];
	}

	render() {
		if (this.isError) {
			return this.renderError();
		}

		return this.renderWatchlist();
	}

	renderWatchlist() {
		const loader = this.renderLoader();
		const table = this.renderProductTable();

		return html`${loader} ${table}`;
	}

	renderProductTable() {
		return html`<x-watchlist-table
			.items="${this.items}"
			.showIsinColumn="${this.showIsinColumn}"
		></x-watchlist-table>`;
	}

	renderLoader() {
		return this.isLoading ? html`<x-loader .isLoading="${this.isLoading}"></x-loader>` : html``;
	}

	renderError() {
		const errorFeedback = translate('watchlist', 'somethingWentWrong', 'Something went wrong. Please try again.');
		const labelRetry = translate('watchlist', 'retry', 'Retry');

		return html`
			<div class="trailer">
				<x-message
					modifier="error"
					icon="warning"
					message="${errorFeedback}"
				></x-message>
				<button
					class="button"
					@click="${this.load}"
				>
					${labelRetry}
				</button>
			</div>
		`;
	}

	connectedCallback() {
		super.connectedCallback();

		this.emptyMessageContainer = document.body.querySelector('.js-empty-message');

		// event getting dispatched from body due to a IE11 bug
		document.body.addEventListener(watchlistEvents.ITEM_REMOVE_SUCCESS, (e) => this.onItemRemovedSuccess(e));
		document.body.addEventListener(alertEvents.ERROR, (data) => this.onItemStoplossAlertError(data));

		this.addEventListener(watchlistEvents.ITEM_REMOVE, this.onItemRemove);
		this.addEventListener(watchlistEvents.ITEM_STOPLOSS_ALERT, this.onItemStoplossAlertEdit);
		this.addEventListener(watchlistEvents.ITEM_MOVE, this.onItemMove);

		this.load();
	}

	onItemRemove({ detail: item }) {
		this.createDialog('x-watchlist-item-remove-form', item);
	}

	onItemRemovedSuccess({ detail: removedItem = {} }) {
		this.items = this.items.filter((i) => i.isin !== removedItem.isin);
	}

	onItemStoplossAlertEdit({ detail: portfolioItem }) {
		this.updateStopLossAlerts(portfolioItem);
	}

	onItemMove() {
		this.items = this.sortItems(this.items);

		const isins = Array.from(this.querySelectorAll('x-watchlist-item')).map((item) => item.id);

		watchlistApi.updateOrder(JSON.stringify({ isins }));

		this.requestUpdate();
	}

	onItemStoplossAlertError({ detail: { isActivated, isin } }) {
		this.syncStopLossAlertStates(isin, !isActivated);
	}

	async updateStopLossAlerts({ isin, hasStopLossLevelReachedNotification: isActivated }) {
		const update = {
			isActivated,
			isin,
			type: 1,
		};

		dispatchCustomEvent(this, alertEvents.UPDATE, update);
	}

	syncStopLossAlertStates(isin, hasStopLossLevelReachedNotification) {
		this.items = this.items.map((item) => {
			if (item.isin === isin) {
				item = {
					...item,
					hasStopLossLevelReachedNotification,
				};
			}

			return item;
		});

		this.requestUpdate();
	}

	createDialog(childTag, watchlistItem) {
		const { productName: title } = watchlistItem;

		return constructDialog({ title }, childTag, { options: JSON.stringify(watchlistItem) });
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-watchlist', Watchlist);

export default Watchlist;
